'use client'

import Cookies from 'js-cookie'

import { resolveClientCache } from '@/core/lib/cache/cache.resolve'
import type { CachePolicyInput } from '@/core/lib/cache/cache.policy'

import {
  createConfigurationFailure,
  executeFetch,
  stringifyBody,
  type FetcherOptions,
  type FetcherResponse,
  type HttpMethod,
} from './core'

type ClientFetcherOptions<
  TBody = unknown,
  TResponse = unknown,
> = FetcherOptions<TBody, TResponse> & {
  authScope?: 'public' | 'session'
  withToast?: boolean
  isFormData?: boolean
  cache?: CachePolicyInput
}

type ReadOptions<TResponse> = Omit<
  ClientFetcherOptions<never, TResponse>,
  'body' | 'isFormData'
>
type WriteOptions<TBody, TResponse> = Omit<
  ClientFetcherOptions<TBody, TResponse>,
  'body' | 'cache'
>

async function request<TResponse, TBody = unknown>(
  method: HttpMethod,
  url: string,
  options: ClientFetcherOptions<TBody, TResponse> = {}
): Promise<FetcherResponse<TResponse>> {
  const {
    body,
    credentials: requestedCredentials,
    headers: customHeaders,
    signal,
    timeoutMs,
    responseSchema,
    requestPolicy,
    idempotencyKey,
    mutationCapabilities,
    telemetry,
    withToast = false,
    isFormData = false,
    cache,
    authScope = 'session',
  } = options
  const credentials =
    authScope === 'public' ? 'omit' : (requestedCredentials ?? 'include')
  const headers: Record<string, string> = {}
  let requestBody: string | FormData | undefined
  let cacheOptions: ReturnType<typeof resolveClientCache> = {}

  try {
    if (authScope === 'public') {
      assertPublicClientHeaders(customHeaders, requestedCredentials)
    } else {
      const csrfToken = Cookies.get('csrftoken')
      if (csrfToken) headers['X-CSRFToken'] = csrfToken
    }
    Object.assign(headers, customHeaders)
    if (
      !isFormData &&
      !Object.keys(headers).some((name) => name.toLowerCase() === 'content-type')
    ) {
      headers['Content-Type'] = 'application/json'
    }
    requestBody =
      isFormData && body instanceof FormData
        ? body
        : stringifyBody(method, body)
    cacheOptions = method === 'GET' ? resolveClientCache(cache) : {}
  } catch {
    const failure = createConfigurationFailure<TResponse>(method, requestPolicy)
    if (withToast) showErrorToast(failure.message)
    return failure
  }

  const response = await executeFetch<TResponse>({
    method,
    url,
    headers,
    body: requestBody,
    credentials,
    signal,
    timeoutMs,
    responseSchema,
    requestPolicy,
    idempotencyKey,
    mutationCapabilities,
    telemetry,
    context: 'client',
    ...cacheOptions,
  })

  if (!response.ok && withToast) {
    showErrorToast(response.message)
  }

  return response
}

function showErrorToast(message: string): void {
  // 선택적 알림이 일반 HTTP의 초기 의존성이나 응답 완료 조건이 되지 않게 한다.
  void import('../toast')
    .then(({ toast }) => toast.error(message))
    .catch(() => {
      // 알림 chunk 실패가 원래 HTTP 결과를 덮어쓰지 않는다.
    })
}

const PUBLIC_FORBIDDEN_HEADERS = new Set([
  'authorization',
  'cookie',
  'x-csrftoken',
  'x-csrf-token',
])

function assertPublicClientHeaders(
  headers: Readonly<Record<string, string>> | undefined,
  credentials: RequestCredentials | undefined
): void {
  if (credentials !== undefined && credentials !== 'omit') {
    throw new Error('Public client requests must omit credentials.')
  }

  const forbidden = Object.keys(headers ?? {}).find((header) =>
    PUBLIC_FORBIDDEN_HEADERS.has(header.toLowerCase())
  )
  if (forbidden) {
    throw new Error(
      'Public client requests cannot carry authentication headers.'
    )
  }
}

/**
 * app/feature 클라이언트 HTTP 래퍼. 일반 요청은 직접 `fetch()` 대신 이 객체를 사용한다.
 * telemetry 같은 core 전송 인프라 예외는 LLM-Guide.md §0의 network boundary를 따른다.
 * mutation 이후 cache invalidation은 인증된 feature Route Handler/Server Action에서 수행한다.
 */
export const http = {
  get: <TResponse>(url: string, options?: ReadOptions<TResponse>) =>
    request<TResponse>('GET', url, options),
  post: <TResponse, TBody = unknown>(
    url: string,
    body?: TBody,
    options?: WriteOptions<TBody, TResponse>
  ) => request<TResponse, TBody>('POST', url, { ...options, body }),
  put: <TResponse, TBody = unknown>(
    url: string,
    body?: TBody,
    options?: WriteOptions<TBody, TResponse>
  ) => request<TResponse, TBody>('PUT', url, { ...options, body }),
  patch: <TResponse, TBody = unknown>(
    url: string,
    body?: TBody,
    options?: WriteOptions<TBody, TResponse>
  ) => request<TResponse, TBody>('PATCH', url, { ...options, body }),
  del: <TResponse>(url: string, options?: WriteOptions<never, TResponse>) =>
    request<TResponse>('DELETE', url, options),
}
